import { Button, Modal, Pagination, Row, Table } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import Swal from 'sweetalert2'
import productApi from '../../api/product.api'
import { userState } from '../../store/user'

const ProductSetOptionComponent = ({
  value,
  onChange,
  currentOptionIndex,
  visible,
  onClose,
  currentProductId, // ✅ 현재 편집 중인 상품의 ID 추가
  product,
}) => {
  const [user, setUser] = useRecoilState(userState)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [modalProductItemList, setModalProductItemList] = useState([])
  const [selectedProducts, setSelectedProducts] = useState([]) // 선택된 상품들
  const [productOptions, setProductOptions] = useState({}) // 각 상품의 옵션들
  const [selectedOptions, setSelectedOptions] = useState({}) // 각 상품의 선택된 옵션들
  const [currentPageNumber, setCurrentPageNumber] = useState(1)
  const [offset, setOffset] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [totalSize, setTotalSize] = useState(0)
  const [inputPrice, setInputPrice] = useState({})

  // visible prop이 변경될 때 모달 상태 업데이트
  useEffect(() => {
    setIsModalVisible(visible)
  }, [visible])

  // ✅ 모달이 열릴 때만 초기화 (중복 제거)
  useEffect(() => {
    if (isModalVisible && value) {
      setSelectedProducts([])
      setSelectedOptions({})
      setProductOptions({})

      // 기존 값이 있으면 inputPrice 상태에 설정
      if (
        value[currentOptionIndex].setProductOriginalPrice ||
        value[currentOptionIndex].setProductPrice
      ) {
        const currentOption = value[currentOptionIndex]
        setInputPrice((prev) => ({
          ...prev,
          [currentOptionIndex]: {
            setProductOriginalPrice: currentOption.setProductOriginalPrice || 0,
            setProductPrice: currentOption.setProductPrice || 0,
          },
        }))
      } else {
        setInputPrice((prev) => ({
          ...prev,
          [currentOptionIndex]: {
            setProductOriginalPrice: 0,
            setProductPrice: 0,
          },
        }))
      }
    }
  }, [isModalVisible, value, currentOptionIndex])

  // ✅ currentOptionIndex가 변경될 때마다 해당 옵션의 상태 복원
  useEffect(() => {
    if (
      isModalVisible &&
      value &&
      Array.isArray(value) &&
      value[currentOptionIndex]
    ) {
      const currentOption = value[currentOptionIndex]

      if (
        currentOption.productSetMappingList &&
        Array.isArray(currentOption.productSetMappingList) &&
        currentOption.productSetMappingList.length > 0
      ) {
        // productSetMappingList가 옵션 ID 배열인 경우
        const optionIds = currentOption.productSetMappingList

        // 옵션 ID들을 상품별로 그룹화
        const optionsByProduct = {}
        const selectedProductsList = []

        // 각 옵션 ID에 대해 상품 정보를 찾아서 복원
        optionIds.forEach(async (optionId) => {
          // 모든 상품의 옵션에서 해당 ID를 찾기
          for (const [productId, productOptionsList] of Object.entries(
            productOptions,
          )) {
            const option = productOptionsList.find((opt) => opt.id === optionId)
            if (option) {
              // 상품 정보 복원
              const product = modalProductItemList.find(
                (p) => p.productId === parseInt(productId),
              )
              if (product) {
                selectedProductsList.push(product)

                // 옵션 선택 상태 복원
                if (!optionsByProduct[productId]) {
                  optionsByProduct[productId] = []
                }
                optionsByProduct[productId].push(optionId)
              }
              break
            }
          }
        })

        setSelectedProducts(selectedProductsList)
        setSelectedOptions(optionsByProduct)

        // 옵션 데이터 미리 로드
        selectedProductsList.forEach((product) => {
          if (product.productId) {
            readProductOption(product.productId)
          }
        })
      } else {
        // 기존 데이터가 없으면 초기화
        setSelectedProducts([])
        setSelectedOptions({})
        setProductOptions({})
      }
    }
  }, [currentOptionIndex, value, isModalVisible])

  // 기존 선택된 옵션들을 자동으로 로드하는 함수
  const loadExistingSelectedOptions = async () => {
    if (
      !value ||
      !value[currentOptionIndex] ||
      !value[currentOptionIndex].productSetMappingList
    ) {
      return
    }

    const optionIds = value[currentOptionIndex].productSetMappingList
    const optionsByProduct = {}
    const selectedProductsList = []

    // 각 옵션 ID에 대해 상품 정보를 찾기
    for (const optionId of optionIds) {
      let found = false

      // 현재 로드된 상품 목록에서 찾기
      for (const product of modalProductItemList) {
        try {
          const param = {
            productId: product.productId,
            exceptOptions: '',
          }
          const res = await productApi.readProductOptionList(param, user.id)

          if (res.data.code === 200) {
            const options =
              res.data.result?.productItemOptionList || res.data.result || []
            const foundOption = options.find((opt) => opt.id === optionId)

            if (foundOption) {
              // 상품 정보 추가
              if (
                !selectedProductsList.some(
                  (p) => p.productId === product.productId,
                )
              ) {
                selectedProductsList.push(product)
              }

              // 옵션 선택 상태 추가
              if (!optionsByProduct[product.productId]) {
                optionsByProduct[product.productId] = []
              }
              optionsByProduct[product.productId].push(optionId)

              // 옵션 데이터 로드
              if (!productOptions[product.productId]) {
                setProductOptions((prev) => ({
                  ...prev,
                  [product.productId]: options,
                }))
              }

              found = true
              break
            }
          } else {
            console.log(`상품 ${product.productId} 옵션 조회 실패:`, res.data)
          }
        } catch (error) {
          console.log('옵션 정보 로드 실패:', error)
        }
      }

      if (!found) {
        console.log(`옵션 ID ${optionId}를 찾을 수 없습니다.`)
      }
    }
    setSelectedProducts(selectedProductsList)
    setSelectedOptions(optionsByProduct)
  }

  // 모달이 열릴 때 기존 선택된 옵션들 자동 로드
  useEffect(() => {
    if (isModalVisible && modalProductItemList.length > 0) {
      loadExistingSelectedOptions()
    }
  }, [isModalVisible, modalProductItemList, value, currentOptionIndex])

  const handleOpenChange = (open) => {
    setIsModalVisible(open)
    if (!open && onClose) {
      onClose()
    }
  }

  const columns = [
    {
      title: '상품번호',
      width: 80,
      dataIndex: 'productId',
      key: 'productId',
      ellipsis: true,
    },
    {
      title: '카테고리',
      width: 120,
      dataIndex: 'categoryName',
      key: 'categoryName',
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: '브랜드명',
      width: 130,
      dataIndex: 'brandName',
      key: 'brandName',
      ellipsis: true,
    },
    {
      title: '상품명',
      width: 150,
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: '상품 상태',
      width: 100,
      dataIndex: 'isPurchase',
      key: 'isPurchase',
      ellipsis: true,
      render: (value, record) => {
        if (record.quantity === 0) {
          return '품절'
        }
        return value ? '판매중' : '미판매'
      },
    },
    {
      title: '노출여부',
      width: 100,
      dataIndex: 'isShow',
      key: 'isShow',
      ellipsis: true,
      render: (value, record) => {
        if (value) {
          return '노출'
        } else {
          return '미노출'
        }
      },
    },
    {
      title: '판매가',
      width: 90,
      dataIndex: 'price1',
      key: 'price1',
      ellipsis: true,
      render: (value) => <span>{value?.toLocaleString()}</span>,
    },
    {
      title: '등록일',
      width: 160,
      dataIndex: 'createdAt',
      key: 'createdAt',
      ellipsis: true,
      render: (value) =>
        value ? dayjs(value).format('YYYY.MM.DD. HH:mm') : '-',
    },
  ]

  const optionColumns = [
    {
      title: '옵션 번호',
      width: 200,
      dataIndex: 'id',
      key: 'id',
      ellipsis: true,
    },
    {
      title: '옵션명',
      width: 200,
      dataIndex: 'optionTitle',
      key: 'optionTitle',
      ellipsis: true,
    },
    {
      title: '옵션 설명',
      width: 200,
      dataIndex: 'optionDescription',
      key: 'optionDescription',
      ellipsis: true,
    },
    {
      title: '판매가',
      width: 100,
      dataIndex: 'price1',
      key: 'price1',
      ellipsis: true,
      render: (value) => <span>{value?.toLocaleString()}</span>,
    },
    {
      title: '할인율',
      width: 80,
      dataIndex: 'discount',
      key: 'discount',
      ellipsis: true,
      render: (value) => <span>{value}%</span>,
    },
    {
      title: '할인가',
      width: 100,
      dataIndex: 'price2',
      key: 'price2',
      ellipsis: true,
      render: (value) => <span>{value?.toLocaleString()}</span>,
    },
  ]

  useEffect(() => {
    if (isModalVisible) {
      readProductList()
    }
  }, [offset, pageSize, isModalVisible])

  const readProductList = async () => {
    try {
      const res = await productApi.readProductList(
        {
          offset: offset,
          pageSize: pageSize,
          productId: null,
          productMakerName: '',
          productName: '',
          brandName: '',
          productStatus: 'total',
          category1: '',
          category2: '',
          category3: '',
          orderType: 'createdAt',
          orderBy: 'desc',
        },
        user.id,
      )

      if (res.data.code === 200) {
        // ✅ 모든 상품을 보여주되, 현재 편집 중인 상품은 디스에이블 처리
        setModalProductItemList(res.data.result.productDTOList)
        setTotalSize(res.data.result.totalCnt)
      } else {
        alert('정상적인 요청이 아닙니다.')
      }
    } catch (error) {
      console.log('readProductList read error', error)
    }
  }

  const readProductOption = async (productId) => {
    try {
      // 이미 선택된 옵션들 수집
      let exceptOptionList = []
      Object.values(selectedOptions).forEach((optionIds) => {
        exceptOptionList = [...exceptOptionList, ...optionIds]
      })

      // ✅ 현재 편집 중인 상품 ID의 옵션들은 제외 목록에 추가
      if (currentProductId) {
        // 현재 상품의 모든 옵션을 제외 목록에 추가
        const currentProductOptions = productOptions[currentProductId] || []
        currentProductOptions.forEach((option) => {
          exceptOptionList.push(option.id)
        })
      }

      const param = {
        productId: productId,
        exceptOptions:
          exceptOptionList.length > 0 ? exceptOptionList.join(',') : '',
      }
      const res = await productApi.readProductOptionList(param, user.id)

      if (res.data.code === 200) {
        // 응답 데이터 구조 확인
        const result = res.data.result

        // result가 배열인지 확인
        if (Array.isArray(result)) {
          setProductOptions((prev) => ({
            ...prev,
            [productId]: result,
          }))
        } else if (result && Array.isArray(result.productItemOptionList)) {
          // API 응답이 객체이고 productItemOptionList가 배열인 경우
          setProductOptions((prev) => ({
            ...prev,
            [productId]: result.productItemOptionList,
          }))
        } else {
          console.log('예상치 못한 응답 구조:', result)
          setProductOptions((prev) => ({
            ...prev,
            [productId]: [],
          }))
        }
      } else {
        alert('옵션 목록을 불러오는데 실패했습니다.')
        setProductOptions((prev) => ({
          ...prev,
          [productId]: [],
        }))
      }
    } catch (error) {
      console.log('readProductOption read error', error)
      setProductOptions((prev) => ({
        ...prev,
        [productId]: [],
      }))
    }
  }

  const handleChangePagination = (pageNumber) => {
    const offset = (pageNumber - 1) * pageSize
    setCurrentPageNumber(pageNumber)
    setOffset(offset)
  }

  const pageSizeChange = (current, pageSize) => {
    setPageSize(pageSize)
    setOffset(0)
    setCurrentPageNumber(1)
  }

  // 상품 클릭 시 옵션 로드
  const handleProductClick = (product) => {
    // ✅ 현재 편집 중인 상품인지 확인
    if (currentProductId && product.productId === currentProductId) {
      alert(
        '현재 편집 중인 상품은 선택할 수 없습니다.\n(회색으로 표시된 상품입니다)',
      )
      return
    }

    // 이미 선택된 상품인지 확인
    const isAlreadySelected = selectedProducts.some(
      (p) => p.productId === product.productId,
    )

    if (!isAlreadySelected) {
      // 새로운 상품 추가
      setSelectedProducts((prev) => [...prev, product])
      // 옵션 로드
      readProductOption(product.productId)
    }
  }

  // 옵션 선택/해제
  const handleOptionSelection = (productId, optionIds) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [productId]: optionIds,
    }))
  }

  // 상품 제거
  const handleRemoveProduct = (productId) => {
    setSelectedProducts((prev) => prev.filter((p) => p.productId !== productId))
    setSelectedOptions((prev) => {
      const newOptions = { ...prev }
      delete newOptions[productId]
      return newOptions
    })
    setProductOptions((prev) => {
      const newOptions = { ...prev }
      delete newOptions[productId]
      return newOptions
    })
  }

  const handleSelect = () => {
    // 선택된 모든 옵션 ID들을 수집
    const selectedOptionIds = []

    selectedProducts.forEach((product) => {
      const selectedOptionIdsForProduct =
        selectedOptions[product.productId] || []
      selectedOptionIdsForProduct.forEach((optionId) => {
        selectedOptionIds.push(optionId)
      })
    })

    // 현재 선택된 가격 정보
    const currentOriginalPrice =
      inputPrice[currentOptionIndex]?.setProductOriginalPrice || 0
    const currentSalePrice =
      inputPrice[currentOptionIndex]?.setProductPrice || 0

    Swal.fire({
      title: '세트 상품 옵션',
      html: `
        <div style="text-align: center;">
          <p><strong>원가:</strong> ${currentOriginalPrice.toLocaleString()}원</p>
          <p><strong>할인가:</strong> ${currentSalePrice.toLocaleString()}원</p>
          <p><strong>선택된 옵션 ID:</strong> ${selectedOptionIds.join(', ') || '없음'}</p>
          <p><strong>선택된 옵션 개수:</strong> ${selectedOptionIds.length}개</p>
        </div>
      `,
      icon: 'warning',
      confirmButtonText: '확인',
      showCancelButton: true,
      cancelButtonText: '취소',
    }).then((result) => {
      if (result.isConfirmed) {
        // 선택된 옵션이 없으면 isSetProduct를 false로 설정
        if (selectedOptionIds.length === 0) {
          if (onChange) {
            const setProductData = {
              isSetProduct: false,
              productSetMappingList: [],
            }
            onChange(setProductData, currentOptionIndex) // 인덱스 정보도 함께 전달
          }
        } else {
          // 선택된 옵션 ID들만 배열로 저장
          const setProductData = {
            isSetProduct: true,
            productSetMappingList: selectedOptionIds, // 옵션 ID 배열
            setProductOriginalPrice: currentOriginalPrice,
            setProductPrice: currentSalePrice,
          }

          // currentOptionIndex에 맞는 특정 인덱스의 값만 전달
          if (onChange) {
            onChange(setProductData, currentOptionIndex) // 인덱스 정보도 함께 전달
          }
        }

        setIsModalVisible(false)
        if (onClose) {
          onClose()
        }
      }
    })
  }

  const handleCancel = () => {
    setIsModalVisible(false)
    setSelectedProducts([])
    setSelectedOptions({})
    setProductOptions({})

    // productSetMappingList가 비어있으면 isSetProduct를 false로 설정
    if (onChange) {
      const currentValue = value && value[currentOptionIndex]
      if (
        currentValue &&
        (!currentValue.productSetMappingList ||
          currentValue.productSetMappingList.length === 0)
      ) {
        const setProductData = {
          ...currentValue,
          isSetProduct: false,
          productSetMappingList: [],
        }
        onChange(setProductData, currentOptionIndex) // 인덱스 정보도 함께 전달
      }
    }

    if (onClose) {
      onClose()
    }
  }

  return (
    <div>
      <Modal
        title="세트 상품 선택"
        open={isModalVisible}
        onOpenChange={handleOpenChange}
        onCancel={handleCancel}
        width={1400}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            취소
          </Button>,
          <Button
            key="select"
            type="primary"
            onClick={handleSelect}
            disabled={
              Object.keys(selectedOptions).length === 0 ||
              inputPrice[currentOptionIndex]?.setProductOriginalPrice ===
                null ||
              inputPrice[currentOptionIndex]?.setProductPrice === null ||
              inputPrice[currentOptionIndex]?.setProductOriginalPrice === 0
            }
          >
            선택 완료
          </Button>,
        ]}
      >
        {/* 세트 상품 가격 입력 */}
        <div
          style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f5f5f5',
            borderRadius: '6px',
          }}
        >
          <h4 style={{ margin: '0 0 12px 0', color: '#1890ff' }}>
            세트 상품 가격 입력
          </h4>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px' }} required>
                원가
              </label>
              <input
                id="setProductOriginalPrice"
                type="text"
                placeholder="예: 10,000"
                value={(
                  inputPrice[currentOptionIndex]?.setProductOriginalPrice || 0
                ).toLocaleString()}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/,/g, '')
                    .replace(/\D/g, '')
                  const numValue = Number(value) || 0
                  setInputPrice((prev) => ({
                    ...prev,
                    [currentOptionIndex]: {
                      ...(prev[currentOptionIndex] || {}),
                      setProductOriginalPrice: numValue,
                    },
                  }))
                }}
                style={{ width: '150px', padding: '4px 8px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px' }} required>
                할인가
              </label>
              <input
                id="setProductPrice"
                type="text"
                placeholder="예: 8,000"
                value={(
                  inputPrice[currentOptionIndex]?.setProductPrice || 0
                ).toLocaleString()}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/,/g, '')
                    .replace(/\D/g, '')
                  const numValue = Number(value) || 0
                  setInputPrice((prev) => ({
                    ...prev,
                    [currentOptionIndex]: {
                      ...(prev[currentOptionIndex] || {}),
                      setProductPrice: numValue,
                    },
                  }))
                }}
                style={{ width: '150px', padding: '4px 8px' }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  setInputPrice((prev) => ({
                    ...prev,
                    [currentOptionIndex]: {
                      ...(prev[currentOptionIndex] || {}),
                      setProductOriginalPrice: product?.price1 || 0,
                      setProductPrice: product?.price2 || 0,
                    },
                  }))
                }}
                style={{ marginTop: '20px' }}
              >
                현재 상품 가격 적용하기
              </Button>
            </div>
          </div>
        </div>
        <div style={{ padding: '20px 0' }}>
          {/* 기존 선택된 세트 옵션 표시 */}
          {value &&
            value[currentOptionIndex] &&
            value[currentOptionIndex].productSetMappingList &&
            value[currentOptionIndex].productSetMappingList.length > 0 && (
              <div
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                }}
              >
                <h4 style={{ margin: '0 0 12px 0', color: '#1890ff' }}>
                  현재 선택된 세트 옵션 (
                  {value[currentOptionIndex].productSetMappingList.length}개)
                </h4>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {value[currentOptionIndex].productSetMappingList.map(
                    (optionId, index) => (
                      <div key={optionId} style={{ marginBottom: '4px' }}>
                        • 옵션 ID: {optionId}
                        {index <
                          value[currentOptionIndex].productSetMappingList
                            .length -
                            1 && ','}
                      </div>
                    ),
                  )}
                </div>
                <div
                  style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}
                >
                  ※ 옵션 상세 정보는 상품을 선택하면 표시됩니다
                </div>
              </div>
            )}

          {/* 상품 목록 */}
          <div style={{ marginBottom: '20px' }}>
            <h4>상품 목록 (클릭하여 옵션 확인)</h4>
            <Table
              columns={columns}
              dataSource={modalProductItemList}
              bordered
              size="middle"
              rowKey="productId"
              scroll={{ x: 'calc(700px + 50%)', y: '40vh' }}
              pagination={false}
              onRow={(record) => ({
                onClick: () => handleProductClick(record),
                style: {
                  cursor:
                    currentProductId && record.productId === currentProductId
                      ? 'not-allowed'
                      : 'pointer',
                  backgroundColor:
                    currentProductId && record.productId === currentProductId
                      ? '#f5f5f5'
                      : 'transparent',
                  opacity:
                    currentProductId && record.productId === currentProductId
                      ? 0.6
                      : 1,
                },
              })}
            />

            <Row justify="end" style={{ marginTop: 16 }}>
              <Pagination
                showQuickJumper
                showSizeChanger
                onShowSizeChange={pageSizeChange}
                total={totalSize}
                onChange={handleChangePagination}
                current={currentPageNumber}
              />
            </Row>
          </div>

          {/* 선택된 상품들의 옵션 */}
          {selectedProducts.length > 0 && (
            <div>
              <h4>선택된 상품 옵션</h4>
              {selectedProducts.map((product) => (
                <div
                  key={product.productId}
                  style={{
                    marginBottom: '20px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px',
                    }}
                  >
                    <h5 style={{ margin: 0 }}>
                      {product.productName} (상품번호: {product.productId})
                    </h5>
                    <Button
                      size="small"
                      danger
                      onClick={() => handleRemoveProduct(product.productId)}
                    >
                      제거
                    </Button>
                  </div>

                  {productOptions[product.productId] ? (
                    <Table
                      rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys:
                          selectedOptions[product.productId] || [],
                        onChange: (selectedRowKeys) =>
                          handleOptionSelection(
                            product.productId,
                            selectedRowKeys,
                          ),
                      }}
                      columns={optionColumns}
                      dataSource={productOptions[product.productId]}
                      bordered
                      size="small"
                      rowKey="id"
                      scroll={{ x: 'calc(600px + 50%)', y: '30vh' }}
                      pagination={false}
                      onRow={(record) => ({
                        onClick: () => {
                          const currentSelectedOptions =
                            selectedOptions[product.productId] || []
                          const optionId = record.id

                          let newSelectedOptions
                          if (currentSelectedOptions.includes(optionId)) {
                            // 이미 선택된 경우 제거
                            newSelectedOptions = currentSelectedOptions.filter(
                              (id) => id !== optionId,
                            )
                          } else {
                            // 선택되지 않은 경우 추가
                            newSelectedOptions = [
                              ...currentSelectedOptions,
                              optionId,
                            ]
                          }

                          handleOptionSelection(
                            product.productId,
                            newSelectedOptions,
                          )
                        },
                        style: {
                          cursor: 'pointer',
                        },
                      })}
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '20px',
                        color: '#999',
                      }}
                    >
                      옵션 로딩 중...
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default ProductSetOptionComponent
